import React, { useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import { isEmpty, keyBy } from "lodash";
import { useForm, useFormState } from "react-hook-form";
import { z } from "zod";

import bullseyeIcon from "@assets/bullseye.svg";
import { useUser } from "@components/user-context";
import { FETCH_CURRENT_BENCHMARKS } from "@gql/benchmarks";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNorthbeamLazyQuery } from "@hooks/http";
import { prettyLogEvent } from "@internal/analytics";
import {
  type FetchCurrentBenchmarks,
  type FetchCurrentBenchmarksVariables,
  type BenchmarkAttributionModelEnum,
  type BenchmarkAttributionWindowEnum,
  type BenchmarkPlatformEnum,
  type BenchmarkTypeEnum,
} from "@north-beam/nb-client-side";
import { Pages } from "@north-beam/nb-client-side/constants";
import { PrimaryButton } from "@shared/buttons";
import Spinner from "@shared/spinner";

import { useUsableBenchmarkWindowsAtom } from "../atoms";
import {
  BenchmarkAttributionWindowOptions,
  BenchmarkPlatformFriendlyNames,
  BenchmarkTypeOptions,
  PrettyBenchmarkAttributionModelNames,
  PrettyBenchmarkAttributionWindowNames,
} from "../constants";
import { useGetBenchmarkPlatformsToUse } from "../hooks";
import { getPrettyBenchmark } from "../utils";

import {
  BenchmarkSubstepWithInput,
  BenchmarkSubstepWithOptions,
} from "./benchmark-choose-and-set-substep";
import { BenchmarkHelpFooter } from "./benchmark-help";

export type ChooseAndSetValidationTargets = {
  [platform in BenchmarkPlatformEnum]: number;
};

const useFetchChooseAndSetData = ({
  attributionModel,
  attributionWindow,
  benchmarkType,
}: {
  attributionModel: BenchmarkAttributionModelEnum;
  attributionWindow: BenchmarkAttributionWindowEnum | null;
  benchmarkType: BenchmarkTypeEnum | null;
}) => {
  const platforms = useGetBenchmarkPlatformsToUse();
  const { loadData, data } = useNorthbeamLazyQuery<
    FetchCurrentBenchmarks,
    FetchCurrentBenchmarksVariables
  >(FETCH_CURRENT_BENCHMARKS);

  useEffect(() => {
    if (attributionWindow && benchmarkType) {
      loadData({
        variables: {
          platforms: platforms,
          benchmarkType,
          attributionModel,
          attributionWindow,
        },
      });
    }
  }, [attributionModel, attributionWindow, benchmarkType, loadData, platforms]);
  return data;
};

type BenchmarkChooseAndSetProps = {
  onNext: (args: {
    benchmarkType: BenchmarkTypeEnum;
    attributionWindow: BenchmarkAttributionWindowEnum;
    targetsByPlatform: ChooseAndSetValidationTargets;
  }) => void;
  currentBenchmarkType: BenchmarkTypeEnum | null;
  currentBenchmarkAttributionWindow: BenchmarkAttributionWindowEnum | null;
  currentBenchmarkTargets?: ChooseAndSetValidationTargets;
  setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
  attributionModel: BenchmarkAttributionModelEnum;
};

export const BenchmarkChooseAndSet = ({
  onNext,
  currentBenchmarkAttributionWindow,
  currentBenchmarkTargets,
  currentBenchmarkType,
  setHasUnsavedChanges,
  attributionModel,
}: BenchmarkChooseAndSetProps) => {
  const { user } = useUser();
  const [usableBenchmarkAttrWindows] = useUsableBenchmarkWindowsAtom();

  const [benchmarkType, setBenchmarkType] = useState<BenchmarkTypeEnum | null>(
    currentBenchmarkType,
  );
  const [attributionWindow, setAttributionWindow] =
    useState<BenchmarkAttributionWindowEnum | null>(
      currentBenchmarkAttributionWindow,
    );

  const platformsToUse = useGetBenchmarkPlatformsToUse();

  const validationSchema = useMemo(() => {
    const numberStringSchema = z.coerce.number().gt(0);
    const schema = platformsToUse.reduce<{
      [platform in BenchmarkPlatformEnum]: typeof numberStringSchema;
    }>(
      (acc, platform) => {
        if (!acc[platform]) {
          acc[platform] = numberStringSchema;
        }
        return acc;
      },
      {} as {
        [platform in BenchmarkPlatformEnum]: typeof numberStringSchema;
      },
    );

    return z.object({ ...schema });
  }, [platformsToUse]);

  const emptyValuesByPlatform = platformsToUse.reduce<{
    [platform in BenchmarkPlatformEnum]: string;
  }>(
    (acc, curr) => {
      if (!acc[curr]) {
        acc[curr] = "";
      }
      return acc;
    },
    {} as {
      [platform in BenchmarkPlatformEnum]: string;
    },
  );

  const {
    handleSubmit,
    control,
    register,
    reset: resetFormTargets,
  } = useForm<ChooseAndSetValidationTargets>({
    defaultValues: currentBenchmarkTargets,
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  });

  useEffect(() => {
    /* we register everything beforehand to ensure that they are always in even if the user never
       navigates to the platform */
    platformsToUse.forEach((platform) => {
      register(`${platform}`);
    });
  }, [register, platformsToUse]);

  const { data, loading } = useFetchChooseAndSetData({
    attributionWindow,
    benchmarkType,
    attributionModel,
  });

  const benchmarkSettingsByPlatform = keyBy(
    data?.me.benchmarks.currentBenchmarks || [],
    "platform",
  );

  const { errors } = useFormState({ control });
  const allSelected = Boolean(benchmarkType && attributionWindow);

  return (
    <>
      {loading && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <Spinner size="lg" />
        </div>
      )}
      <div
        className={clsx("flex flex-col h-full", {
          "opacity-25": loading,
        })}
      >
        <div className="flex items-center mr-20">
          <div className="text-lg font-semibold">
            Setup your 1-day benchmark for the{" "}
            <span className="text-primary">
              {PrettyBenchmarkAttributionModelNames[
                attributionModel
              ].toLowerCase()}
            </span>{" "}
            attribution model
          </div>
          <img
            src={bullseyeIcon}
            alt="bullseye"
            className="ml-3 mb-[0.3rem]"
            style={{
              height: "2rem",
              width: "auto",
            }}
          />
        </div>
        <div className="mt-2 mb-8 mr-20 lg:mr-0 max-w-[42rem] text-sm lg:text-base">
          Create 1-day CAC and ROAS benchmarks for the &apos;Clicks Only&apos;
          and/or &apos;Clicks + Modeled Views&apos; attribution models based on
          your longer-term team targets.
        </div>
        <div className="flex flex-col gap-y-8 pb-12 overflow-auto flex-grow pl-1 pr-7">
          <div className="flex flex-col lg:flex-row gap-4">
            <BenchmarkSubstepWithOptions
              testId="metric-selector"
              placeholder="Select metric..."
              onChange={(val) => {
                setBenchmarkType(val);
                setHasUnsavedChanges(true);
                // casting it here because i need to put add defaults
                resetFormTargets(
                  emptyValuesByPlatform as unknown as ChooseAndSetValidationTargets,
                );
              }}
              title="Select benchmark metric:"
              subHeader="A benchmark metric is a unit for measuring your performance over time"
              stepNumber={1}
              options={BenchmarkTypeOptions}
              tooltip={
                <div>
                  ROAS is a strong indicator of short-term ad efficiency,
                  whereas CAC tracks the ongoing cost of building your customer
                  transaction base.
                  <div className="">
                    MER is a measure of profitability and is calculated by
                    taking the total spend divided by the total revenue of the
                    business
                  </div>
                </div>
              }
              currentValue={benchmarkType}
              disable={loading}
            />
            <div className="flex-1"></div>
          </div>
          <div className="flex flex-col lg:flex-row gap-4">
            <BenchmarkSubstepWithOptions
              testId="timeframe-selector"
              placeholder="Select timeframe..."
              className="mb-8"
              onChange={(val) => {
                setAttributionWindow(val);
                setHasUnsavedChanges(true);
                resetFormTargets(
                  emptyValuesByPlatform as unknown as ChooseAndSetValidationTargets,
                );
              }}
              title="Select the time frame used to plan your target:"
              subHeader={
                "The time frame is the period through which benchmarks " +
                "will be measured. 30 days is a suitable attribution window for most use cases."
              }
              stepNumber={2}
              options={BenchmarkAttributionWindowOptions.map((option) => {
                const isDisabled = !usableBenchmarkAttrWindows?.has(
                  option.value,
                );
                if (isDisabled) {
                  return {
                    ...option,
                    label: `${option.label} - Not enough data to use`,
                  };
                }
                return option;
              })}
              isOptionDisabled={(option) => {
                return Boolean(!usableBenchmarkAttrWindows?.has(option.value));
              }}
              tooltip={
                "Time frames for benchmarks should align with the time " +
                "frames over which you determine and measure your wider business goals."
              }
              currentValue={attributionWindow}
              disable={loading}
            />
            <div className="flex-1"></div>
          </div>
          {attributionWindow &&
            benchmarkType &&
            platformsToUse.map((platform) => {
              return (
                <div key={platform} className="flex flex-col lg:flex-row gap-4">
                  <BenchmarkSubstepWithInput
                    loading={loading}
                    title={`Enter your target ${BenchmarkPlatformFriendlyNames[platform]} ${PrettyBenchmarkAttributionWindowNames[attributionWindow]} ${benchmarkType}`}
                    control={control}
                    register={register}
                    subHeader={`Your target is the ${benchmarkType} goal your business needs to meet.`}
                    tooltip={`Your ${benchmarkType} target must be close to your historical, statistically significant ${benchmarkType} results`}
                    benchmarkType={benchmarkType}
                    stepNumber={`3 - ${BenchmarkPlatformFriendlyNames[platform]}`}
                    disabled={loading}
                    placeholder="Benchmark target"
                    cellRegistration={platform}
                  />
                  <BenchmarkCalculatedRate
                    benchmarkType={benchmarkType}
                    currentBenchmark={benchmarkSettingsByPlatform[platform]}
                    loading={loading}
                    attributionModel={attributionModel}
                    platform={platform}
                  />
                </div>
              );
            })}
        </div>
        <BenchmarkHelpFooter>
          <PrimaryButton
            onClick={handleSubmit((validTargets) => {
              if (!benchmarkType || !attributionWindow) {
                return;
              }

              onNext({
                benchmarkType,
                attributionWindow,
                targetsByPlatform: validTargets,
              });

              prettyLogEvent.onSubmit({
                page: Pages.SALES,
                user,
                domain: "Benchmarking - Modal",
                additionalActionInfo: "choose-and-set-modal",
                data: {
                  attributionModel,
                  attributionWindow,
                  benchmark: benchmarkType,
                  targets: validTargets,
                },
              });
            })}
            isDisabled={!allSelected || !isEmpty(errors)}
          >
            CONTINUE
          </PrimaryButton>
        </BenchmarkHelpFooter>
      </div>
    </>
  );
};

const BenchmarkCalculatedRate = ({
  benchmarkType,
  currentBenchmark,
  loading,
  attributionModel,
  platform,
}: {
  platform: BenchmarkPlatformEnum;
  benchmarkType: BenchmarkTypeEnum;
  attributionModel: BenchmarkAttributionModelEnum;
  currentBenchmark?: FetchCurrentBenchmarks["me"]["benchmarks"]["currentBenchmarks"][number];
  loading: boolean;
}) => {
  const { user } = useUser();
  return (
    <div className="flex-1 flex justify-center items-center h-28 bg-cosmic-4 rounded-2xl py-4">
      <div>
        <div className="text-sm">
          Your current {BenchmarkPlatformFriendlyNames[platform]}{" "}
          <span className="font-bold text-primary">
            {PrettyBenchmarkAttributionModelNames[
              attributionModel
            ].toLowerCase()}
          </span>{" "}
          {benchmarkType} is:
        </div>
        <div className="text-2xl font-bold">
          {getPrettyBenchmark(
            benchmarkType,
            loading ? undefined : currentBenchmark?.benchmark,
            user,
          )}
        </div>
      </div>
    </div>
  );
};
